import { useEffect, useRef, useState } from 'react';

const VOLUME_KEY = 'eplAnthemVolume';
const LAST_TRACK_INDEX_KEY = 'eplAnthemLastTrackIndex';
const YT_PLAYLIST_ID = 'PLlmyYmqoMXCsDoF4-oS_cTnwT3Qli-ibh';
const DEFAULT_VOLUME = 0.08;

const BackgroundAnthems = () => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const savedVolumeRef = useRef(DEFAULT_VOLUME);
  const tabAutoMutedRef = useRef(false);
  const userActivatedRef = useRef(false);
  const volumeRef = useRef(DEFAULT_VOLUME);
  const [playerReady, setPlayerReady] = useState(false);
  const [volume, setVolume] = useState(() => {
    const raw = localStorage.getItem(VOLUME_KEY);
    const n = raw == null ? DEFAULT_VOLUME : Number(raw);
    const clamped = Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : DEFAULT_VOLUME;
    return Math.min(clamped, DEFAULT_VOLUME);
  });

  volumeRef.current = volume;
  savedVolumeRef.current = tabAutoMutedRef.current ? savedVolumeRef.current : volume;

  const applyAudibleVolume = (player, nextVolume = volumeRef.current) => {
    if (!player?.setVolume) return;
    const level = Math.round(Math.min(1, Math.max(0, nextVolume)) * 100);
    player.unMute?.();
    player.setVolume(level);
  };

  const startPlaylist = (player) => {
    const playlist = player.getPlaylist?.() || [];
    if (playlist.length > 0 && player.playVideoAt) {
      const lastIndexRaw = localStorage.getItem(LAST_TRACK_INDEX_KEY);
      const lastIndex = lastIndexRaw == null ? -1 : Number(lastIndexRaw);

      let nextIndex = Math.floor(Math.random() * playlist.length);
      if (playlist.length > 1 && Number.isFinite(lastIndex) && nextIndex === lastIndex) {
        nextIndex = (nextIndex + 1 + Math.floor(Math.random() * (playlist.length - 1))) % playlist.length;
      }

      localStorage.setItem(LAST_TRACK_INDEX_KEY, String(nextIndex));
      player.playVideoAt(nextIndex);
      return;
    }
    player.playVideo?.();
  };

  useEffect(() => {
    const ensureYoutubeApi = () =>
      new Promise((resolve) => {
        if (window.YT?.Player) {
          resolve();
          return;
        }

        const existing = document.getElementById('youtube-iframe-api');
        if (!existing) {
          const script = document.createElement('script');
          script.id = 'youtube-iframe-api';
          script.src = 'https://www.youtube.com/iframe_api';
          document.body.appendChild(script);
        }

        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (typeof prev === 'function') prev();
          resolve();
        };

        const poll = window.setInterval(() => {
          if (window.YT?.Player) {
            window.clearInterval(poll);
            resolve();
          }
        }, 50);
        window.setTimeout(() => window.clearInterval(poll), 10000);
      });

    let mounted = true;
    let host = null;
    let playNudgeTimer = 0;

    ensureYoutubeApi().then(() => {
      if (!mounted || !window.YT?.Player || !containerRef.current || playerRef.current) return;

      host = document.createElement('div');
      host.id = 'yt-anthems-player';
      containerRef.current.appendChild(host);

      playerRef.current = new window.YT.Player(host, {
        height: '1',
        width: '1',
        playerVars: {
          listType: 'playlist',
          list: YT_PLAYLIST_ID,
          autoplay: 1,
          controls: 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
        },
        events: {
          onReady: (event) => {
            if (!mounted) return;
            const player = event.target;
            setPlayerReady(true);

            // Start with sound when the browser allows it.
            applyAudibleVolume(player, volumeRef.current);
            player.setShuffle(true);
            startPlaylist(player);

            playNudgeTimer = window.setTimeout(() => {
              if (!mounted || !playerRef.current) return;
              try {
                const state = player.getPlayerState?.();
                if (state === -1 || state === 0 || state === 2 || state === 5) {
                  player.playVideo?.();
                }
              } catch (_) {
                /* ignore */
              }
            }, 400);
          },
          onStateChange: (event) => {
            if (event.data === window.YT?.PlayerState?.PLAYING && !event.target.isMuted?.()) {
              userActivatedRef.current = true;
            }
          },
        },
      });
    });

    return () => {
      mounted = false;
      window.clearTimeout(playNudgeTimer);
      if (playerRef.current?.destroy) {
        playerRef.current.destroy();
      }
      playerRef.current = null;
      if (host?.parentNode) {
        host.parentNode.removeChild(host);
      }
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(VOLUME_KEY, String(volume));
    if (playerRef.current?.setVolume && userActivatedRef.current && !tabAutoMutedRef.current) {
      applyAudibleVolume(playerRef.current, volume);
    }
  }, [volume]);

  useEffect(() => {
    const activateAudio = () => {
      const player = playerRef.current;
      if (userActivatedRef.current || !player?.setVolume) return;

      userActivatedRef.current = true;
      applyAudibleVolume(player, volumeRef.current);
      player.playVideo?.();
    };

    // Keep listeners until activation succeeds — early clicks before ready must retry.
    const opts = { capture: true };
    window.addEventListener('pointerdown', activateAudio, opts);
    window.addEventListener('keydown', activateAudio, opts);
    window.addEventListener('touchstart', activateAudio, opts);
    window.addEventListener('click', activateAudio, opts);

    return () => {
      window.removeEventListener('pointerdown', activateAudio, opts);
      window.removeEventListener('keydown', activateAudio, opts);
      window.removeEventListener('touchstart', activateAudio, opts);
      window.removeEventListener('click', activateAudio, opts);
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!playerRef.current?.setVolume) return;

      if (document.hidden && !tabAutoMutedRef.current) {
        tabAutoMutedRef.current = true;
        savedVolumeRef.current = volumeRef.current;
        setVolume(0);
        playerRef.current.mute?.();
        playerRef.current.setVolume(0);
      } else if (!document.hidden && tabAutoMutedRef.current) {
        tabAutoMutedRef.current = false;
        const restored = savedVolumeRef.current || DEFAULT_VOLUME;
        setVolume(restored);
        if (userActivatedRef.current) {
          applyAudibleVolume(playerRef.current, restored);
          playerRef.current.playVideo?.();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      aria-hidden={!playerReady}
      style={{
        position: 'fixed',
        width: 1,
        height: 1,
        bottom: 0,
        left: 0,
        opacity: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: -1,
      }}
    />
  );
};

export default BackgroundAnthems;
